/**
 * Datos de Horarios › Turnos y Cuadro de turnos (008 · T041 · US1; contracts/admin-api.md A1–A6 y
 * A15–A17).
 *
 * - Plantillas: `GET /shift-templates?status`, `GET /shift-templates/{id}`, `POST /shift-templates`,
 *   `PATCH /shift-templates/{id}`, `POST /shift-templates/{id}/versions`,
 *   `POST /shift-templates/{id}/archive`.
 * - Cuadro: `GET /schedule-grid` (cursor opaco por trabajadores, ≤ 42 días),
 *   `POST /schedule-grid/cells` (todo o nada, ≤ 500 celdas) y `GET /schedule-grid/history`.
 *
 * Toda respuesta se valida en runtime con el esquema Zod de `@fermin/contracts`. Sin optimistic UI
 * (wiring §11.11): la pantalla cambia solo cuando la API confirma. Cambiar una plantilla invalida
 * también el cuadro (sus celdas muestran el código y el horario de la plantilla).
 *
 * - Feriados (T075, A18–A20): `GET /holidays?year&site_id&include_removed`, `POST /holidays`,
 *   `POST /holidays/{id}/remove`.
 * - Patrones (T087, A7–A14): `GET|POST /schedule-patterns`, `GET|PATCH /schedule-patterns/{id}`,
 *   `POST /schedule-patterns/{id}/archive`, `GET|POST /schedule-pattern-assignments` (con
 *   `?dry_run=true` no escribe) y `POST /schedule-pattern-assignments/{id}/end`.
 *
 * Archivo compartido en serie (tasks.md): T041 → T075 (feriados) → T087 (patrones).
 */
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type UseInfiniteQueryResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useMemo } from 'react';

import {
  ApplyPatternResponseSchema,
  ConflictReasonSchema,
  GridHistoryResponseSchema,
  HolidayListResponseSchema,
  HolidaySchema,
  PatternApplicationListResponseSchema,
  SchedulePatternDetailSchema,
  SchedulePatternListResponseSchema,
  SchedulePatternSchema,
  WorkerSchedulePatternSchema,
  SaveGridCellsResponseSchema,
  ScheduleGridResponseSchema,
  ShiftTemplateDetailSchema,
  ShiftTemplateListResponseSchema,
  type ApplyPatternBody,
  type ApplyPatternResponse,
  type CatalogStatusFilter,
  type CreateHolidayBody,
  type CreateShiftTemplateBody,
  type CreateShiftTemplateVersionBody,
  type GridHistoryResponse,
  type Holiday,
  type PatternApplicationListResponse,
  type SaveGridCellsBody,
  type SaveGridCellsResponse,
  type ScheduleGridResponse,
  type SchedulePattern,
  type SchedulePatternBody,
  type SchedulePatternDetail,
  type ShiftTemplate,
  type ShiftTemplateDetail,
  type UpdateSchedulePatternBody,
  type UpdateShiftTemplateBody,
  type Worker,
  type WorkerSchedulePattern,
} from '@fermin/contracts';

import { api, type ApiError } from '../../api/client';
import { t } from '../../i18n';
import { flattenWorkerPages, useWorkers, type WorkerFilters } from '../workers/api';

export const SHIFT_TEMPLATES_QUERY_KEY = ['shift-templates'] as const;
export const SCHEDULE_GRID_QUERY_KEY = ['schedule-grid'] as const;
export const HOLIDAYS_QUERY_KEY = ['holidays'] as const;
export const SCHEDULE_PATTERNS_QUERY_KEY = ['schedule-patterns'] as const;
export const PATTERN_APPLICATIONS_QUERY_KEY = ['schedule-pattern-assignments'] as const;
/**
 * Clave raíz del tareo (`TIMESHEET_QUERY_KEY` de `features/timesheet/api.ts`, de otro lote): los
 * feriados, ausencias y patrones cambian su evaluación. Se repite el literal para no acoplar este
 * archivo al módulo del tareo.
 */
export const TIMESHEET_ROOT_QUERY_KEY = ['timesheet'] as const;

/**
 * Invalida lo que depende del plan de los días: el cuadro (feriados, ausencias y días generados) y
 * el tareo (su evaluación). Lo usan feriados, ausencias y patrones.
 */
export function useInvalidatePlanViews(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: SCHEDULE_GRID_QUERY_KEY }),
      queryClient.invalidateQueries({ queryKey: TIMESHEET_ROOT_QUERY_KEY }),
    ]);
  };
}

// ---------------------------------------------------------------------------
// Plantillas de turno (A1–A6)
// ---------------------------------------------------------------------------

/** `GET /shift-templates?status` (defecto de la API: `ACTIVE`). */
export function shiftTemplatesPath(status: CatalogStatusFilter = 'ACTIVE'): string {
  return `/shift-templates?status=${status}`;
}

export function useShiftTemplates(
  status: CatalogStatusFilter = 'ACTIVE',
): UseQueryResult<ShiftTemplate[], ApiError> {
  return useQuery<ShiftTemplate[], ApiError>({
    queryKey: [...SHIFT_TEMPLATES_QUERY_KEY, 'list', status],
    queryFn: () =>
      api.get(shiftTemplatesPath(status), ShiftTemplateListResponseSchema).then((r) => r.items),
  });
}

/** `GET /shift-templates/{id}` con todas sus versiones (descendente). `404` fuera de la empresa. */
export function useShiftTemplate(id: string | null): UseQueryResult<ShiftTemplateDetail, ApiError> {
  return useQuery<ShiftTemplateDetail, ApiError>({
    queryKey: [...SHIFT_TEMPLATES_QUERY_KEY, 'detail', id],
    queryFn: () => api.get(`/shift-templates/${id!}`, ShiftTemplateDetailSchema),
    enabled: id !== null,
  });
}

/** Invalida plantillas (lista y detalle) y el cuadro. */
function useInvalidateTemplates(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: SHIFT_TEMPLATES_QUERY_KEY }),
      queryClient.invalidateQueries({ queryKey: SCHEDULE_GRID_QUERY_KEY }),
    ]);
  };
}

/** `POST /shift-templates` → `201 ShiftTemplateDetail`. */
export function useCreateShiftTemplate() {
  const invalidate = useInvalidateTemplates();
  return useMutation<ShiftTemplateDetail, ApiError, CreateShiftTemplateBody>({
    mutationFn: (body) => api.post('/shift-templates', body, ShiftTemplateDetailSchema),
    onSuccess: invalidate,
  });
}

export interface CreateTemplateVersionVars {
  id: string;
  body: CreateShiftTemplateVersionBody;
}

/** `POST /shift-templates/{id}/versions` — nueva versión con `effective_from`. */
export function useCreateTemplateVersion() {
  const invalidate = useInvalidateTemplates();
  return useMutation<ShiftTemplateDetail, ApiError, CreateTemplateVersionVars>({
    mutationFn: ({ id, body }) =>
      api.post(`/shift-templates/${id}/versions`, body, ShiftTemplateDetailSchema),
    onSuccess: invalidate,
  });
}

export interface UpdateShiftTemplateVars {
  id: string;
  body: UpdateShiftTemplateBody;
}

/** `PATCH /shift-templates/{id}` — nombre y código (sin cambio real ⇒ 200 sin auditoría). */
export function useUpdateShiftTemplate() {
  const invalidate = useInvalidateTemplates();
  return useMutation<ShiftTemplateDetail, ApiError, UpdateShiftTemplateVars>({
    mutationFn: ({ id, body }) =>
      api.patch(`/shift-templates/${id}`, body, ShiftTemplateDetailSchema),
    onSuccess: invalidate,
  });
}

/** `POST /shift-templates/{id}/archive` — deja de ofrecerse; nunca se borra. */
export function useArchiveShiftTemplate() {
  const invalidate = useInvalidateTemplates();
  return useMutation<ShiftTemplateDetail, ApiError, { id: string }>({
    mutationFn: ({ id }) =>
      api.post(`/shift-templates/${id}/archive`, {}, ShiftTemplateDetailSchema),
    onSuccess: invalidate,
  });
}

// ---------------------------------------------------------------------------
// Cuadro de turnos (A15–A17)
// ---------------------------------------------------------------------------

/** Filtros aplicados del cuadro (`ScheduleGridQuerySchema` sin `cursor`/`limit`). */
export interface ScheduleGridFilters {
  from_day: string;
  to_day: string;
  site_id?: string;
  worker_id?: string;
  search?: string;
}

const GRID_FILTER_KEYS = ['from_day', 'to_day', 'site_id', 'worker_id', 'search'] as const;

/** `GET /schedule-grid?from_day&to_day&site_id&worker_id&search&cursor`. */
export function scheduleGridPath(filters: ScheduleGridFilters, cursor?: string): string {
  const params = new URLSearchParams();
  for (const key of GRID_FILTER_KEYS) {
    const value = filters[key]?.trim();
    if (value) {
      params.set(key, value);
    }
  }
  if (cursor) {
    params.set('cursor', cursor);
  }
  return `/schedule-grid?${params.toString()}`;
}

/** Clave exacta de las páginas del cuadro (la que recorta `RefreshButton`). */
export function scheduleGridKey(filters: ScheduleGridFilters | null): unknown[] {
  return [...SCHEDULE_GRID_QUERY_KEY, 'page', filters];
}

/**
 * Cuadro paginado por trabajadores ("Cargar más"). `filters = null` no consulta (p. ej. mientras
 * no hay sede elegida o el rango no es válido).
 */
export function useScheduleGrid(
  filters: ScheduleGridFilters | null,
): UseInfiniteQueryResult<{ pages: ScheduleGridResponse[] }, ApiError> {
  return useInfiniteQuery<
    ScheduleGridResponse,
    ApiError,
    { pages: ScheduleGridResponse[] },
    unknown[],
    string | null
  >({
    queryKey: scheduleGridKey(filters),
    queryFn: ({ pageParam }) =>
      api.get(scheduleGridPath(filters!, pageParam ?? undefined), ScheduleGridResponseSchema),
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.next_cursor,
    enabled: filters !== null,
  });
}

/** `POST /schedule-grid/cells` — una transacción; con cualquier error no se guarda nada. */
export function useSaveGridCells() {
  const queryClient = useQueryClient();
  return useMutation<SaveGridCellsResponse, ApiError, SaveGridCellsBody>({
    mutationFn: (body) => api.post('/schedule-grid/cells', body, SaveGridCellsResponseSchema),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: SCHEDULE_GRID_QUERY_KEY });
    },
  });
}

/** Recarga el cuadro (tras `409 SCHEDULE_CHANGED`: lo que hay en pantalla ya no es la verdad). */
export function useInvalidateScheduleGrid(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: SCHEDULE_GRID_QUERY_KEY });
  };
}

/** Celda cuyo historial se consulta. */
export interface GridCellRef {
  workerId: string;
  day: string;
}

/** `GET /schedule-grid/history?worker_id&day` — versiones de la celda, descendentes. */
export function useCellHistory(
  cell: GridCellRef | null,
): UseQueryResult<GridHistoryResponse, ApiError> {
  return useQuery<GridHistoryResponse, ApiError>({
    queryKey: [...SCHEDULE_GRID_QUERY_KEY, 'history', cell?.workerId ?? null, cell?.day ?? null],
    queryFn: () => {
      const params = new URLSearchParams({ worker_id: cell!.workerId, day: cell!.day });
      return api.get(`/schedule-grid/history?${params.toString()}`, GridHistoryResponseSchema);
    },
    enabled: cell !== null,
  });
}

// ---------------------------------------------------------------------------
// Feriados (A18–A20)
// ---------------------------------------------------------------------------

/** Filtros aplicados de feriados (`HolidayListQuery`). */
export interface HolidayFilters {
  year: number;
  site_id?: string;
  include_removed?: boolean;
}

/** `GET /holidays?year&site_id&include_removed`. */
export function holidaysPath(filters: HolidayFilters): string {
  const params = new URLSearchParams({ year: String(filters.year) });
  if (filters.site_id) {
    params.set('site_id', filters.site_id);
  }
  if (filters.include_removed) {
    params.set('include_removed', 'true');
  }
  return `/holidays?${params.toString()}`;
}

export function useHolidays(filters: HolidayFilters): UseQueryResult<Holiday[], ApiError> {
  return useQuery<Holiday[], ApiError>({
    queryKey: [...HOLIDAYS_QUERY_KEY, 'list', filters],
    queryFn: () => api.get(holidaysPath(filters), HolidayListResponseSchema).then((r) => r.items),
  });
}

function useInvalidateHolidays(): () => Promise<void> {
  const queryClient = useQueryClient();
  const invalidatePlan = useInvalidatePlanViews();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: HOLIDAYS_QUERY_KEY }),
      invalidatePlan(),
    ]);
  };
}

/** `POST /holidays` → `201 Holiday`. Duplicado activo ⇒ `409 CONFLICT` (`field: 'day'`). */
export function useCreateHoliday() {
  const invalidate = useInvalidateHolidays();
  return useMutation<Holiday, ApiError, CreateHolidayBody>({
    mutationFn: (body) => api.post('/holidays', body, HolidaySchema),
    onSuccess: invalidate,
  });
}

/** `POST /holidays/{id}/remove` (cuerpo `{}`): queda en el historial como retirado. */
export function useRemoveHoliday() {
  const invalidate = useInvalidateHolidays();
  return useMutation<Holiday, ApiError, { id: string }>({
    mutationFn: ({ id }) => api.post(`/holidays/${id}/remove`, {}, HolidaySchema),
    onSuccess: invalidate,
  });
}

// ---------------------------------------------------------------------------
// Patrones (A7–A11)
// ---------------------------------------------------------------------------

/** `GET /schedule-patterns?status` (defecto de la API: `ACTIVE`). */
export function schedulePatternsPath(status: CatalogStatusFilter = 'ACTIVE'): string {
  return `/schedule-patterns?status=${status}`;
}

export function useSchedulePatterns(
  status: CatalogStatusFilter = 'ACTIVE',
): UseQueryResult<SchedulePattern[], ApiError> {
  return useQuery<SchedulePattern[], ApiError>({
    queryKey: [...SCHEDULE_PATTERNS_QUERY_KEY, 'list', status],
    queryFn: () =>
      api.get(schedulePatternsPath(status), SchedulePatternListResponseSchema).then((r) => r.items),
  });
}

/** `GET /schedule-patterns/{id}` con `active_applications`. */
export function useSchedulePattern(
  id: string | null,
): UseQueryResult<SchedulePatternDetail, ApiError> {
  return useQuery<SchedulePatternDetail, ApiError>({
    queryKey: [...SCHEDULE_PATTERNS_QUERY_KEY, 'detail', id],
    queryFn: () => api.get(`/schedule-patterns/${id!}`, SchedulePatternDetailSchema),
    enabled: id !== null,
  });
}

function useInvalidatePatterns(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: SCHEDULE_PATTERNS_QUERY_KEY });
  };
}

/** `POST /schedule-patterns` → `201 SchedulePattern`. */
export function useCreateSchedulePattern() {
  const invalidate = useInvalidatePatterns();
  return useMutation<SchedulePattern, ApiError, SchedulePatternBody>({
    mutationFn: (body) => api.post('/schedule-patterns', body, SchedulePatternSchema),
    onSuccess: invalidate,
  });
}

export interface UpdateSchedulePatternVars {
  id: string;
  body: UpdateSchedulePatternBody;
}

/** `PATCH /schedule-patterns/{id}` — nombre y/o días (tipo y ciclo son inmutables). */
export function useUpdateSchedulePattern() {
  const invalidate = useInvalidatePatterns();
  return useMutation<SchedulePattern, ApiError, UpdateSchedulePatternVars>({
    mutationFn: ({ id, body }) =>
      api.patch(`/schedule-patterns/${id}`, body, SchedulePatternSchema),
    onSuccess: invalidate,
  });
}

/** `POST /schedule-patterns/{id}/archive` — las aplicaciones activas siguen hasta su fin. */
export function useArchiveSchedulePattern() {
  const invalidate = useInvalidatePatterns();
  return useMutation<SchedulePattern, ApiError, { id: string }>({
    mutationFn: ({ id }) => api.post(`/schedule-patterns/${id}/archive`, {}, SchedulePatternSchema),
    onSuccess: invalidate,
  });
}

// ---------------------------------------------------------------------------
// Aplicaciones de patrones (A12–A14)
// ---------------------------------------------------------------------------

/** Filtros aplicados de aplicaciones (`PatternApplicationListQuery` sin `cursor`/`limit`). */
export interface PatternApplicationFilters {
  worker_id?: string;
  site_id?: string;
  pattern_id?: string;
  status?: 'ACTIVE' | 'ENDED' | 'ALL';
}

const APPLICATION_FILTER_KEYS = ['worker_id', 'site_id', 'pattern_id', 'status'] as const;

/** `GET /schedule-pattern-assignments?worker_id&site_id&pattern_id&status&cursor`. */
export function patternApplicationsPath(
  filters: PatternApplicationFilters,
  cursor?: string,
): string {
  const params = new URLSearchParams();
  for (const key of APPLICATION_FILTER_KEYS) {
    const value = filters[key]?.trim();
    if (value) {
      params.set(key, value);
    }
  }
  if (cursor) {
    params.set('cursor', cursor);
  }
  const query = params.toString();
  return query ? `/schedule-pattern-assignments?${query}` : '/schedule-pattern-assignments';
}

/** Clave exacta de las páginas de aplicaciones (la que recorta `RefreshButton`). */
export function patternApplicationsKey(filters: PatternApplicationFilters): unknown[] {
  return [...PATTERN_APPLICATIONS_QUERY_KEY, 'page', filters];
}

export function usePatternApplications(
  filters: PatternApplicationFilters,
): UseInfiniteQueryResult<{ pages: PatternApplicationListResponse[] }, ApiError> {
  return useInfiniteQuery<
    PatternApplicationListResponse,
    ApiError,
    { pages: PatternApplicationListResponse[] },
    unknown[],
    string | null
  >({
    queryKey: patternApplicationsKey(filters),
    queryFn: ({ pageParam }) =>
      api.get(
        patternApplicationsPath(filters, pageParam ?? undefined),
        PatternApplicationListResponseSchema,
      ),
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.next_cursor,
  });
}

/** Invalida aplicaciones, patrones (`active_applications`), el cuadro y el tareo. */
function useInvalidateApplications(): () => Promise<void> {
  const queryClient = useQueryClient();
  const invalidatePlan = useInvalidatePlanViews();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: PATTERN_APPLICATIONS_QUERY_KEY }),
      queryClient.invalidateQueries({ queryKey: SCHEDULE_PATTERNS_QUERY_KEY }),
      invalidatePlan(),
    ]);
  };
}

/** `POST /schedule-pattern-assignments[?dry_run=true]`. */
export function applyPatternPath(dryRun: boolean): string {
  return dryRun ? '/schedule-pattern-assignments?dry_run=true' : '/schedule-pattern-assignments';
}

export interface ApplyPatternVars {
  body: ApplyPatternBody;
  /** `true` ⇒ solo calcula el resumen (`200`), sin escribir ni invalidar. */
  dryRun: boolean;
}

/** `POST /schedule-pattern-assignments` → `201` (o `200` con `dry_run`) `ApplyPatternResponse`. */
export function useApplyPattern() {
  const invalidate = useInvalidateApplications();
  return useMutation<ApplyPatternResponse, ApiError, ApplyPatternVars>({
    mutationFn: ({ body, dryRun }) =>
      api.post(applyPatternPath(dryRun), body, ApplyPatternResponseSchema),
    onSuccess: async (_data, vars) => {
      if (!vars.dryRun) {
        await invalidate();
      }
    },
  });
}

/** `POST /schedule-pattern-assignments/{id}/end` — los días posteriores pasan a `OFF` salvo manuales. */
export function useEndPatternApplication() {
  const invalidate = useInvalidateApplications();
  return useMutation<WorkerSchedulePattern, ApiError, { id: string; end_day: string }>({
    mutationFn: ({ id, end_day }) =>
      api.post(`/schedule-pattern-assignments/${id}/end`, { end_day }, WorkerSchedulePatternSchema),
    onSuccess: invalidate,
  });
}

// ---------------------------------------------------------------------------
// Opciones de trabajadores (ausencias y aplicación de patrones)
// ---------------------------------------------------------------------------

export interface WorkerOption {
  value: string;
  label: string;
}

/** "Ana Ruiz · A-01" (el código interno ayuda a distinguir homónimos). */
export function workerOptionLabel(
  worker: Pick<Worker, 'first_names' | 'last_names' | 'internal_code'>,
): string {
  const name = `${worker.first_names} ${worker.last_names}`.trim();
  return worker.internal_code ? `${name} · ${worker.internal_code}` : name;
}

/**
 * Trabajadores del alcance como opciones de `Combobox` (primera página de `GET /workers`; la API
 * recorta por alcance).
 */
export function useWorkerOptions(filters: WorkerFilters = {}): {
  options: WorkerOption[];
  isPending: boolean;
} {
  const query = useWorkers(filters);
  const options = useMemo(
    () =>
      flattenWorkerPages(query.data).map((worker) => ({
        value: worker.id,
        label: workerOptionLabel(worker),
      })),
    [query.data],
  );
  return { options, isPending: query.isPending };
}

// ---------------------------------------------------------------------------
// Errores
// ---------------------------------------------------------------------------

/** `true` si es el `409 CONFLICT` con `details.reason = SCHEDULE_CHANGED`. */
export function isScheduleChanged(error: ApiError): boolean {
  if (!error.is('CONFLICT')) {
    return false;
  }
  const reason = ConflictReasonSchema.safeParse(error.details?.['reason']);
  return reason.success && reason.data === ConflictReasonSchema.enum.SCHEDULE_CHANGED;
}

/** `true` si es `409 CONFLICT` con `details.field = 'status'` (patrón archivado, aplicación terminada). */
export function isStatusConflict(error: ApiError): boolean {
  return error.is('CONFLICT') && error.details?.['field'] === 'status';
}

/**
 * Texto de un error de horarios: `SCHEDULE_CHANGED` con texto propio; el resto desde el catálogo
 * común (`PERIOD_CLOSED`, `INTEGRATION_MANAGED`, `READ_ONLY`, …) o, si no lo hay, el mensaje es-PE
 * de la API.
 */
export function scheduleErrorMessage(error: ApiError): string {
  if (isScheduleChanged(error)) {
    return t('schedules.errors.SCHEDULE_CHANGED');
  }
  const key = `common.errors.${error.code}`;
  const text = t(key);
  return text === key ? error.message : text;
}
